/**
 * ANGLES TO WRITE: what people search about the story that nobody answers well,
 * most searched first - and the way out of the room: tick the ones worth a post
 * and send them to Publish, where each becomes a topic with its searches as
 * keywords and the story so far as background (`boardroom/story_publish.py`).
 */
import { useState } from 'react';
import type { BoardroomAngles } from '../../../types';
import { VerdictBadge } from '../ItemCard';
import { CARD } from './tableLayout';

export interface SentTo {
  project_id: number;
  project_name: string;
  made: boolean;
  topics: { angle_id: number; already: boolean }[];
}

/** "Sent 2 angles to the new subject Loughlin scandal" and the like. Pure. */
export function sentWords(sent: SentTo): string {
  const fresh = sent.topics.filter(t => !t.already).length;
  const again = sent.topics.length - fresh;
  const where = `${sent.made ? 'the new subject' : 'the subject'} “${sent.project_name}”`;
  const main = fresh ? `Sent ${fresh} angle${fresh === 1 ? '' : 's'} to ${where}` : `Already in ${where}`;
  return again && fresh ? `${main} (${again} already there)` : main;
}

export default function AnglesReport({
  data,
  onOpen,
  onSend,
  canPublish,
}: {
  data: BoardroomAngles;
  onOpen: (id: number) => void;
  /** Send the ticked angles to Publish. Absent without a room to send from. */
  onSend?: (angleIds: number[]) => Promise<SentTo>;
  /** Whether this account holds Publish, where a subject lives. */
  canPublish?: boolean;
}) {
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<SentTo | null>(null);
  const [error, setError] = useState('');
  const toggle = (id: number) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  if (data.rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No angles yet. The coverage watcher reads the room&apos;s searches and adds the ones nobody answers well on the
        next dig.
      </p>
    );
  }

  const send = async () => {
    if (!onSend || !picked.length || busy) return;
    setBusy(true);
    setError('');
    try {
      setSent(await onSend(picked));
      setPicked([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send them');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="angles-report">
      <p className="text-xs text-gray-400">
        {data.totals.angles} angle{data.totals.angles === 1 ? '' : 's'} · {data.totals.volume.toLocaleString('en-US')}{' '}
        searches a month between them
      </p>
      <ul className={`${CARD} divide-y divide-gray-100 dark:divide-gray-700`}>
        {data.rows.map(a => (
          <li key={a.id} className="flex items-start gap-3 p-3">
            {onSend && canPublish && (
              <input
                type="checkbox"
                className="mt-1"
                checked={picked.includes(a.id)}
                onChange={() => toggle(a.id)}
                aria-label={`Pick ${a.title}`}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpen(a.id)}
                  className="text-left text-sm font-medium text-gray-900 hover:underline dark:text-gray-100"
                >
                  {a.title}
                </button>
                {a.verdict && <VerdictBadge verdict={a.verdict} />}
              </div>
              {a.body && <p className="text-xs text-gray-600 dark:text-gray-300">{a.body}</p>}
              {a.why && <p className="text-xs text-amber-700 dark:text-amber-300">Missing: {a.why}</p>}
              {a.searches.length > 0 && (
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{a.searches.join(' · ')}</p>
              )}
            </div>
            <div className="shrink-0 text-right text-xs tabular-nums text-gray-600 dark:text-gray-300">
              {a.volume ? `${a.volume.toLocaleString('en-US')}/mo` : <span className="text-gray-400">no volume</span>}
            </div>
          </li>
        ))}
      </ul>
      {onSend && canPublish && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={!picked.length || busy}
            onClick={() => void send()}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500"
          >
            {busy ? 'Sending…' : `Send ${picked.length || ''} to Publish`.replace('  ', ' ')}
          </button>
          {sent && (
            <span className="text-xs text-emerald-700 dark:text-emerald-300" data-testid="angles-sent">
              {sentWords(sent)} ·{' '}
              <a href={`/subjects/${sent.project_id}`} className="underline">
                open it
              </a>
            </span>
          )}
          {error && (
            <span className="text-xs text-red-600 dark:text-red-400" role="alert">
              {error}
            </span>
          )}
        </div>
      )}
      {onSend && !canPublish && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Publish turns these into pages; it is not on this account.
        </p>
      )}
    </div>
  );
}
